import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  Eye,
  Loader2,
  UserRound,
  X,
  XCircle,
} from "lucide-react";
import { IAccreditationEvent, IEventApcap } from "../@types/system";
import { Button } from "./Button";
import api from "../service/api";
import { useStore } from "../store";
import { formatDate, hasPermission } from "../utils/helpers";
import { maskCnpj, maskCpf } from "../utils/masks";
import { toastError, toastSuccess } from "../utils/toast";

interface IProps {
  data: IEventApcap;
  closeCredentials: (val: boolean) => void;
  idEvent: string;
  idClient: string;
}

interface ICheckInItem {
  id: string;
  participant_id: string;
  event_id: string;
  created_at: string;
  updated_at: string;
}

interface IParticipantPresence {
  participant: IAccreditationEvent;
  event: {
    id: string;
    name: string;
    initial_date: string;
    finish_date: string;
  };
  checkIns: ICheckInItem[];
  missingDates: string[];
  eventDates: string[];
}

function formatDayLabel(dateStr: string) {
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return dateStr;
  return new Date(year, month - 1, day).toLocaleDateString("pt-BR");
}

function formatWeekday(dateStr: string) {
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return "";
  const label = new Date(year, month - 1, day).toLocaleDateString("pt-BR", {
    weekday: "long",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function toBrazilDateKey(value: string | Date) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(value));
}

function formatCheckInTime(value: string | Date) {
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function formatDocument(document?: string | null) {
  if (!document) return "Sem documento";
  const digits = document.replace(/\D/g, "");
  if (digits.length === 11) return maskCpf(digits);
  if (digits.length === 14) return maskCnpj(digits);
  return document;
}

function positionLabel(position?: string) {
  if (position === "SERVIDOR_PUBLICO") return "Servidor Público";
  if (position === "PARLAMENTAR") return "Parlamentar";
  return position || "Cargo não informado";
}

function getInitials(name?: string) {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function countEventDays(initialDate?: string, finishDate?: string) {
  if (!initialDate || !finishDate) return 0;
  const startMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(initialDate);
  const endMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(finishDate);
  if (!startMatch || !endMatch) return 0;

  const start = new Date(
    Date.UTC(Number(startMatch[1]), Number(startMatch[2]) - 1, Number(startMatch[3]))
  );
  const end = new Date(
    Date.UTC(Number(endMatch[1]), Number(endMatch[2]) - 1, Number(endMatch[3]))
  );

  if (start.getTime() > end.getTime()) return 0;

  let count = 0;
  const cursor = new Date(start);
  while (cursor.getTime() <= end.getTime()) {
    count += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}

const PresenceParticipants = ({
  data,
  closeCredentials,
  idEvent,
  idClient,
}: IProps) => {
  const user = useStore((store) => store.user);

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [participants, setParticipants] = useState<IAccreditationEvent[]>([]);
  const [presenceCounts, setPresenceCounts] = useState<Record<string, number>>(
    {}
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] =
    useState<IAccreditationEvent | null>(null);
  const [presenceDetail, setPresenceDetail] =
    useState<IParticipantPresence | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [markingDate, setMarkingDate] = useState<string | null>(null);

  const eventDaysTotal = useMemo(
    () => countEventDays(data.initial_date, data.finish_date),
    [data.finish_date, data.initial_date]
  );

  const list = useMemo(() => {
    const sorted = [...participants].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", "pt-BR")
    );

    if (!search.trim()) {
      return sorted;
    }

    const term = search.toLowerCase();
    return sorted.filter(
      (item) =>
        item.name?.toLowerCase().includes(term) ||
        item.document?.toLowerCase().includes(term)
    );
  }, [search, participants]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = list.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.max(1, Math.ceil(list.length / itemsPerPage));

  const timeline = useMemo(() => {
    if (!presenceDetail) return [];

    const checkInByDay = new Map(
      presenceDetail.checkIns.map((checkIn) => [
        toBrazilDateKey(checkIn.created_at),
        checkIn,
      ])
    );

    return presenceDetail.eventDates.map((day) => {
      const checkIn = checkInByDay.get(day) || null;
      return {
        day,
        weekday: formatWeekday(day),
        label: formatDayLabel(day),
        present: Boolean(checkIn),
        checkIn,
      };
    });
  }, [presenceDetail]);

  const summary = useMemo(() => {
    if (!presenceDetail) {
      return { present: 0, missing: 0, total: 0, percent: 0 };
    }
    const total = presenceDetail.eventDates.length;
    const present = Math.min(presenceDetail.checkIns.length, total);
    const missing = presenceDetail.missingDates.length;
    const percent = total > 0 ? Math.round((present / total) * 100) : 0;
    return { present, missing, total, percent };
  }, [presenceDetail]);

  const handleData = useCallback(async () => {
    setLoading(true);
    try {
      const ownerId =
        user.user_type === "ADMIN" || user.user_type === "SUPERADMIN"
          ? idClient || user.id
          : user.id;

      const [resp, presenceResp] = await Promise.all([
        api.get(`events/accreditation/event/${idEvent}`),
        api.get<ICheckInItem[]>(`events/presence/${idEvent}/${ownerId}`),
      ]);

      const rows: IAccreditationEvent[] = (resp.data || []).filter(
        (row: IAccreditationEvent) => row.user_id === ownerId
      );

      const counts: Record<string, number> = {};
      for (const checkIn of presenceResp.data || []) {
        counts[checkIn.participant_id] =
          (counts[checkIn.participant_id] || 0) + 1;
      }

      setParticipants(rows);
      setPresenceCounts(counts);
    } catch (error) {
      console.log("[ERROR]: ", error);
      toastError("Erro ao carregar participantes");
    } finally {
      setLoading(false);
    }
  }, [idClient, idEvent, user.id, user.user_type]);

  const loadParticipantPresence = useCallback(
    async (participant: IAccreditationEvent) => {
      setLoadingDetail(true);
      try {
        const { data: detail } = await api.get<IParticipantPresence>(
          `events/presence/participant/${participant.id}`,
          {
            params: { event_id: idEvent },
          }
        );
        setPresenceDetail(detail);
      } catch (error: unknown) {
        const message =
          (
            error as {
              response?: { data?: { message?: string; error?: string } };
            }
          )?.response?.data?.message ||
          (
            error as {
              response?: { data?: { message?: string; error?: string } };
            }
          )?.response?.data?.error ||
          "Erro ao carregar check-ins";
        toastError(message);
        setPresenceDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [idEvent]
  );

  const openPresenceModal = useCallback(
    async (participant: IAccreditationEvent) => {
      setSelectedParticipant(participant);
      setPresenceDetail(null);
      setModalOpen(true);
      await loadParticipantPresence(participant);
    },
    [loadParticipantPresence]
  );

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setSelectedParticipant(null);
    setPresenceDetail(null);
    setMarkingDate(null);
  }, []);

  const handleMarkPresence = useCallback(
    async (date: string) => {
      if (!selectedParticipant) return;
      if (!hasPermission(user.user_type)) {
        toastError("Apenas administradores podem marcar presença");
        return;
      }

      setMarkingDate(date);
      try {
        await api.post("events/presence/manual", {
          event_id: idEvent,
          participant_id: selectedParticipant.id,
          date,
        });
        toastSuccess("Presença marcada com sucesso");
        await loadParticipantPresence(selectedParticipant);
        setPresenceCounts((prev) => ({
          ...prev,
          [selectedParticipant.id]: (prev[selectedParticipant.id] || 0) + 1,
        }));
      } catch (error: unknown) {
        const message =
          (
            error as {
              response?: { data?: { message?: string; error?: string } };
            }
          )?.response?.data?.message ||
          (
            error as {
              response?: { data?: { message?: string; error?: string } };
            }
          )?.response?.data?.error ||
          "Erro ao marcar presença";
        toastError(message);
      } finally {
        setMarkingDate(null);
      }
    },
    [idEvent, loadParticipantPresence, selectedParticipant, user.user_type]
  );

  useEffect(() => {
    handleData();
  }, [handleData]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage]);

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
  };

  const participantCard = selectedParticipant || presenceDetail?.participant;

  return (
    <>
      {loading && (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-zinc-500">
          <Loader2 className="h-5 w-5 animate-spin text-apcap" />
          Carregando participantes...
        </div>
      )}

      {!loading && (
        <div className="overflow-x-auto rounded-t-lg">
          <div className="p-4 bg-white dark:bg-zinc-800">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-xl font-bold text-gray-700 dark:text-white shrink-0">
                {data.name}
              </h2>

              <div className="flex w-full items-center gap-2 sm:w-auto">
                <input
                  type="text"
                  placeholder="Pesquisar participante"
                  className="w-full min-h-[44px] p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-apcap focus:border-apcap sm:w-64"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => closeCredentials(false)}
                >
                  Voltar
                </Button>
              </div>
            </div>
          </div>

          <table className="min-w-full divide-y-2 divide-gray-200 bg-white text-sm text-left">
            <thead className="ltr:text-left rtl:text-right max-w-screen">
              <tr>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                  Nome
                </th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                  Documento
                </th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                  Cargo
                </th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                  Presenças
                </th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900">
                  Cadastro
                </th>
                <th className="whitespace-nowrap px-4 py-2 font-medium text-gray-900 text-right">
                  #
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentItems.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-4 text-gray-500">
                    Nenhum participante encontrado
                  </td>
                </tr>
              )}
              {currentItems.map((participant) => {
                const presentCount = presenceCounts[participant.id] || 0;
                const isComplete =
                  eventDaysTotal > 0 && presentCount >= eventDaysTotal;

                return (
                <tr
                  key={participant.id}
                  className="hover:bg-gray-100 cursor-pointer"
                  onClick={() => openPresenceModal(participant)}
                >
                  <td className="px-4 py-2 font-medium text-gray-900">
                    {participant.name}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {formatDocument(participant.document)}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {positionLabel(participant.position)}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    <span
                      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                        isComplete
                          ? "bg-emerald-100 text-emerald-800"
                          : presentCount > 0
                            ? "bg-amber-100 text-amber-800"
                            : "bg-zinc-100 text-zinc-600"
                      }`}
                    >
                      {eventDaysTotal > 0
                        ? `${presentCount} / ${eventDaysTotal}`
                        : presentCount}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {formatDate(participant.created_at)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      className="inline-flex items-center justify-center"
                      aria-label={`Ver check-ins de ${participant.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openPresenceModal(participant);
                      }}
                    >
                      <Eye className="h-4 w-4 text-apcap" />
                    </Button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && (
        <div className="flex flex-col gap-3 rounded-b-lg bg-white shadow-sm px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-2 items-center">
            <select
              name="qtd-pages"
              id="qtd-pages"
              className="inline-flex items-center justify-center rounded border 
                    border-gray-100 bg-white text-gray-900 cursor-pointer"
              onChange={(e) => setItemsPerPage(Number(e.target.value))}
              value={itemsPerPage}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>

            <div className="text-gray-700"> de {list.length}</div>
          </div>

          <ol className="flex justify-end gap-1 text-xs font-medium">
            <li>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180 ${
                  currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <span className="sr-only">Prev Page</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </li>

            {(() => {
              const pageNumbers = [];
              let startPage = Math.max(1, currentPage - 2);
              let endPage = Math.min(totalPages, currentPage + 2);

              if (currentPage <= 3) {
                startPage = 1;
                endPage = Math.min(totalPages, 5);
              } else if (currentPage >= totalPages - 2) {
                startPage = Math.max(1, totalPages - 4);
                endPage = totalPages;
              }

              for (let i = startPage; i <= endPage; i++) {
                pageNumbers.push(
                  <li key={i}>
                    <button
                      onClick={() => handlePageChange(i)}
                      className={`block size-8 rounded border border-gray-100 text-center leading-8 text-gray-900 ${
                        currentPage === i ? "bg-apcap text-white" : ""
                      }`}
                    >
                      {i}
                    </button>
                  </li>
                );
              }

              return (
                <>
                  {startPage > 1 && (
                    <li>
                      <span className="block size-8 text-center text-gray-500 pt-2">
                        ...
                      </span>
                    </li>
                  )}
                  {pageNumbers}
                  {endPage < totalPages && (
                    <li>
                      <span className="block size-8 text-center text-gray-500 pt-2">
                        ...
                      </span>
                    </li>
                  )}
                </>
              );
            })()}

            <li>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`inline-flex size-8 items-center justify-center rounded border border-gray-100 bg-white text-gray-900 rtl:rotate-180 ${
                  currentPage === totalPages
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
              >
                <span className="sr-only">Next Page</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </li>
          </ol>
        </div>
      )}

      <Transition.Root show={modalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-30" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-zinc-950/50 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative w-full max-h-[92vh] transform overflow-hidden rounded-2xl border border-zinc-200/80 bg-white text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl">
                  <div className="border-b border-zinc-100 bg-gradient-to-br from-apcap-muted/70 via-white to-white px-5 pb-5 pt-5 sm:px-6">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-start gap-3">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-apcap text-sm font-semibold text-white">
                          {getInitials(participantCard?.name)}
                        </div>
                        <div className="min-w-0">
                          <Dialog.Title
                            as="h3"
                            className="truncate text-lg font-semibold text-zinc-900"
                          >
                            {participantCard?.name || "Participante"}
                          </Dialog.Title>
                          <p className="mt-0.5 text-sm text-zinc-600">
                            {formatDocument(participantCard?.document)}
                          </p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <span className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-xs font-medium text-apcap ring-1 ring-apcap/20">
                              {positionLabel(participantCard?.position)}
                            </span>
                            {participantCard?.departament ? (
                              <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-medium text-zinc-700">
                                {participantCard.departament}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={closeModal}
                        className="rounded-lg p-1.5 text-zinc-400 hover:bg-white/80 hover:text-zinc-700"
                        aria-label="Fechar"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  <div className="max-h-[calc(92vh-7.5rem)] overflow-y-auto px-5 py-5 sm:px-6">
                    {loadingDetail && (
                      <div className="flex flex-col items-center justify-center gap-3 py-16 text-sm text-zinc-500">
                        <Loader2 className="h-6 w-6 animate-spin text-apcap" />
                        Carregando presença do participante...
                      </div>
                    )}

                    {!loadingDetail && presenceDetail && (
                      <div className="space-y-6">
                        <section>
                          <div className="mb-3 flex items-center gap-2 text-sm font-medium text-zinc-900">
                            <UserRound className="h-4 w-4 text-apcap" />
                            Resumo de presença
                          </div>

                          <div className="grid grid-cols-3 gap-2 sm:gap-3">
                            <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-3">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-emerald-700/80">
                                Presentes
                              </p>
                              <p className="mt-1 text-2xl font-semibold text-emerald-800">
                                {summary.present}
                              </p>
                            </div>
                            <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-3">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-amber-700/80">
                                Faltantes
                              </p>
                              <p className="mt-1 text-2xl font-semibold text-amber-800">
                                {summary.missing}
                              </p>
                            </div>
                            <div className="rounded-xl border border-apcap/15 bg-apcap-muted/60 px-3 py-3">
                              <p className="text-[11px] font-medium uppercase tracking-wide text-apcap/80">
                                Frequência
                              </p>
                              <p className="mt-1 text-2xl font-semibold text-apcap">
                                {summary.percent}%
                              </p>
                            </div>
                          </div>

                          <div className="mt-3">
                            <div className="mb-1.5 flex items-center justify-between text-xs text-zinc-500">
                              <span>
                                {summary.present} de {summary.total} dias
                              </span>
                              <span>
                                {summary.missing === 0
                                  ? "Presença completa"
                                  : `${summary.missing} dia(s) pendente(s)`}
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-zinc-100">
                              <div
                                className="h-full rounded-full bg-apcap transition-all duration-500"
                                style={{ width: `${summary.percent}%` }}
                              />
                            </div>
                          </div>
                        </section>

                        <section>
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 text-sm font-medium text-zinc-900">
                              <CalendarDays className="h-4 w-4 text-apcap" />
                              Linha do tempo do evento
                            </div>
                            <span className="text-xs text-zinc-500">
                              {formatDayLabel(
                                presenceDetail.event.initial_date
                              )}{" "}
                              —{" "}
                              {formatDayLabel(presenceDetail.event.finish_date)}
                            </span>
                          </div>

                          {timeline.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-500">
                              Este evento não possui dias configurados.
                            </div>
                          ) : (
                            <ol className="relative space-y-3 before:absolute before:left-[1.15rem] before:top-3 before:h-[calc(100%-1.5rem)] before:w-px before:bg-zinc-200">
                              {timeline.map((item) => (
                                <li
                                  key={item.day}
                                  className="relative flex gap-3 rounded-xl border border-zinc-100 bg-zinc-50/60 p-3 sm:p-3.5"
                                >
                                  <div
                                    className={`relative z-10 mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-4 ring-white ${
                                      item.present
                                        ? "bg-emerald-100 text-emerald-700"
                                        : "bg-amber-100 text-amber-700"
                                    }`}
                                  >
                                    {item.present ? (
                                      <CheckCircle2 className="h-4 w-4" />
                                    ) : (
                                      <CircleDashed className="h-4 w-4" />
                                    )}
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                      <div className="min-w-0">
                                        <p className="text-sm font-semibold text-zinc-900">
                                          {item.weekday}
                                        </p>
                                        <p className="text-sm text-zinc-600">
                                          {item.label}
                                        </p>
                                        {item.present && item.checkIn ? (
                                          <p className="mt-1 text-xs text-emerald-700">
                                            Check-in às{" "}
                                            {formatCheckInTime(
                                              item.checkIn.created_at
                                            )}
                                            {" · "}
                                            registrado em{" "}
                                            {formatDate(
                                              item.checkIn.updated_at,
                                              true
                                            )}
                                          </p>
                                        ) : (
                                          <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
                                            <XCircle className="h-3.5 w-3.5" />
                                            Presença não registrada
                                          </p>
                                        )}
                                      </div>

                                      <div className="shrink-0">
                                        {item.present ? (
                                          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
                                            Presente
                                          </span>
                                        ) : hasPermission(user.user_type) ? (
                                          <Button
                                            type="button"
                                            disabled={markingDate === item.day}
                                            onClick={() =>
                                              handleMarkPresence(item.day)
                                            }
                                            className="min-h-[36px] px-3 py-1.5 text-xs"
                                          >
                                            {markingDate === item.day ? (
                                              <span className="inline-flex items-center gap-1.5">
                                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                Salvando
                                              </span>
                                            ) : (
                                              "Marcar presença"
                                            )}
                                          </Button>
                                        ) : (
                                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                                            Ausente
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ol>
                          )}
                        </section>

                        {summary.missing === 0 && summary.total > 0 && (
                          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                            Presença completa em todos os dias do evento.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default PresenceParticipants;
